import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import { useLocation, useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import {
  getPersonnelListUploadTemplate,
  saveTransactionDetails,
  validatePersonnelListFile,
} from "../../../api/TransactionApisV2";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../../utils/DownloadFile";
import convertToBase64 from "../../../utils/convertToBase64";
import { TransactionDetailsData } from "../../../types/Transaction";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import { useDeleteTransaction } from "../../../queries/transactionV2";

interface PersonnelListUploadedProps {
  onStepChange: (e?: number) => void;
  onBackClick: (e?: number) => void;
}

const PersonnelListUploaded = (props: PersonnelListUploadedProps) => {
  const formik = useFormikContext<TransactionDetailsData>();
  const location = useLocation();
  const params = useParams();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;
  const [file, setFile] = useState<File | undefined>(undefined);
  const [errors, setErrors] = useState<string[]>([]);
  const verifyTemplate = async (file: File) => {
    const base64File = (await convertToBase64(file)) as string;
    formik.setFieldValue("file", base64File);
    validatePersonnelListFile({
      file: base64File,
      existingTransaction: mode === "edit",
    })
      .then((res) => formik.setFieldValue("previewList", res.data))
      .catch((err) => setErrors(err.response.data.errorRaw.split(",")));
  };
  const { acceptedFiles, getRootProps, getInputProps, isDragActive } =
    useDropzone({
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
      },
      disabled: mode === "edit" && formik.values.eventState !== "DRAFT",
      maxFiles: 1,
    });

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFile(file);
      setErrors([]);
      verifyTemplate(file);
    }
  }, [acceptedFiles]);

  const handleBackClick = () => {
    if (formik.values.monetaryDetails?.considerBlackOutDays) {
      props.onBackClick(3);
    } else {
      props.onBackClick(2);
    }
  };

  const handlePreviewClick = () => {
    if (errors.length > 0) {
      return;
    }
    if (file === undefined && mode !== "edit") {
      setErrors(["Please upload a file"]);
      return;
    }
    if (!formik.values.previewList || formik.values.previewList?.length === 0) {
      setErrors(["No Employees were there in the uploaded file"]);
      return;
    }
    formik.setFieldValue("monetaryDetails.isFileUploaded", true);
    formik.setFieldValue("selectedEmployeesList", formik.values.previewList);
    if (!formik.values.id) {
      saveTransactionDetails(formik.values);
    }
    props.onStepChange();
  };
  const { mutate: deleteTransaction } = useDeleteTransaction();
  const navigate = useNavigate();

  const handleDiscard = () => {
    if (
      formik.values.id &&
      formik.values.eventState === MonetaryEventState.DRAFT
    ) {
      deleteTransaction(formik.values.id, {
        onSuccess: () => {
          toast.success("Draft Exercise Discarded Successfully");
          navigate("/options/esopTransactions");
        },
        onError: (error: any) => {
          toast(error.response.data.errorRaw, {
            type: "error",
            autoClose: 5000,
          });
        },
      });
    } else {
      navigate("/options/esopTransactions");
    }
  };

  const downloadListTemplate = async () => {
    try {
      const uploadTemplate = await getPersonnelListUploadTemplate();
      const template = uploadTemplate.data.uploadTemplate;
      const blobObject = await convertBase64ToBlob(
        template,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
      downloadBlobObject(blobObject, `Exercise_Personnel_List.xlsx`);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errorRaw ||
          err?.message ||
          "Failed to download template. Please try again."
      );
    }
  };
  return (
    <VStack className="justify-start w-full h-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between text-xl font-semibold text-black-501">
          Participant List
        </HStack>
        <HStack className="flex text-sm font-medium text-gray-500">
          Your filtered list of participants: View of all employees who qualify
          for this exercise event based on the rules and filters you configured
          during setup. Use this to track and manage participation effectively.
        </HStack>
      </VStack>
      <VStack className="gap-6 px-6 py-4">
        <VStack className="px-20 pt-4">
          <div>
            <div
              className={`flex flex-col items-center justify-center w-[100%] h-[150px] border-2 border-dashed border-[#0c224a] rounded-lg cursor-pointer bg-[#f6f9ff] hover:bg-gray-200 transition ${
                mode === "edit" && formik.values.eventState !== "DRAFT"
                  ? "cursor-not-allowed pointer-events-none"
                  : ""
              }`}
            >
              <div
                {...getRootProps()}
                className={`flex flex-col items-center justify-center w-full h-full ${
                  mode === "edit" && formik.values.eventState !== "DRAFT"
                    ? "opacity-50 cursor-not-allowed pointer-events-none"
                    : ""
                }`}
              >
                <input {...getInputProps()} aria-label="File upload input" />
                <Icon
                  icon="material-symbols:upload-sharp"
                  color="#E85936"
                  height={32}
                />
                <p className="text-sm text-gray-600">
                  Drag and drop or{" "}
                  <span className="text-[#0327e4]">
                    <u>browse</u>
                  </span>{" "}
                  your files
                </p>
                {file ? (
                  <HStack className="items-center gap-2 pt-2 text-center">
                    <p className="text-sm font-medium text-gray-700">
                      Uploaded: {file.name}
                    </p>
                    <Icon
                      icon={"mdi:trash"}
                      height={18}
                      width={18}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFile(undefined);
                        setErrors([]);
                      }}
                    />
                  </HStack>
                ) : (
                  <p className="pt-2 text-sm text-gray-600">
                    {mode === "edit" && formik.values.eventState !== "DRAFT"
                      ? "Cannot change Uploaded file"
                      : mode === "edit" && formik.values.eventState === "DRAFT"
                      ? "Upload File to Change Existing"
                      : "No file uploaded yet."}
                  </p>
                )}
              </div>
            </div>
            {errors.length > 0 && (
              <div className="p-2 mt-6 overflow-auto border border-red-600 bg-[#c728280d] rounded max-h-48">
                <HStack className="gap-2">
                  <Icon
                    icon={"material-symbols:error"}
                    color="#c72828"
                    height={20}
                  />
                  <p className="font-medium text-red-600">
                    {errors.length} Errors Found
                  </p>
                </HStack>
                <ul className="text-red-600 list-disc">
                  {errors.map((err, index) => (
                    <li className="pt-1 text-xs mx-14" key={index}>
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </VStack>
        <VStack>
          <div className="pt-8 pb-2 text-base font-medium ">
            <div className="flex justify-start pl-6">
              Download the excel template
            </div>
            <div className="pt-1 text-xs text-gray-600 mx-14">
              The downloaded file contains two sheets:
              <ul className="pt-2 pl-6 list-disc">
                <li>
                  <strong>Input Sheet:</strong> Use this sheet to input employee
                  details.
                </li>
                <li>
                  <strong>Reference Sheet:</strong> This sheet provides
                  reference data for eligible options. Employees cannot be
                  assigned more options than listed here. If an employee is not
                  listed, they do not have any eligible options.
                </li>
              </ul>
            </div>
          </div>
          <HStack
            className="items-center justify-between gap-2 p-4 px-8 text-center border-2 rounded-lg cursor-pointer"
            onClick={downloadListTemplate}
          >
            <HStack className="items-center gap-2 text-center ">
              <Icon icon="vscode-icons:file-type-excel" height={30} />
              <p className="text-base font-medium ">
                Exercise_Personnel_List.xlsx
              </p>
            </HStack>
            <HStack className="items-center ">
              <Icon
                className=""
                icon="material-symbols:download-sharp"
                height={24}
              />
            </HStack>
          </HStack>
        </VStack>
      </VStack>
      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>
        <HStack className="gap-2">
          {formik.values.eventState === MonetaryEventState.DRAFT && (
            <ButtonPrimary1 className="" onClick={handleDiscard}>
              Discard
            </ButtonPrimary1>
          )}
          <ButtonPrimary
            className="text-white bg-orange-500 hover:bg-orange-600"
            onClick={handlePreviewClick}
          >
            Preview
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default PersonnelListUploaded;
